import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Package } from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { companiesService } from "../services/companiesService";
import { catalogService } from "../services/catalogService";
import type { Company, Product, Service } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { ProductCard } from "../components/ProductCard";
import { EmptyState } from "../components/EmptyState";
import { useTranslation } from "react-i18next";

export function CompanyProductsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { selectedEventId } = useAppStore();
  const [company, setCompany] = useState<Company | undefined>();
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const found = await companiesService.getById(id, selectedEventId ?? undefined);
        if (!cancelled) setCompany(found);
        if (found && selectedEventId) {
          const [prods, srvs] = await Promise.all([
            catalogService.listProducts(selectedEventId, found.id),
            catalogService.listServices(selectedEventId, found.id),
          ]);
          if (!cancelled) {
            setProducts(prods);
            setServices(srvs);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, selectedEventId]);

  if (loading) {
    return (
      <PageShell>
        <div className="pt-20 text-center text-gray-500">{t("common.loading")}</div>
      </PageShell>
    );
  }

  if (!company) {
    return (
      <PageShell>
        <div className="pt-20 text-center text-gray-500">{t("companies.not_found")}</div>
      </PageShell>
    );
  }

  return (
    <PageShell noPadding>
      <PageHeader
        title={t("visitor.company_products_services")}
        subtitle={company.name}
        back
        backTo={`/companies/${company.id}`}
      />

      <div className="px-4 pt-4 pb-24 space-y-6">
        {/* Products Section */}
        {products.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-[16px] font-medium text-gray-900 mb-3">
              {t("visitor.products_count", { count: products.length })}
            </h2>
            <div className="space-y-2">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant="full"
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Services Section */}
        {services.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <h2 className="text-[16px] font-medium text-gray-900 mb-3">
              {t("visitor.services_count", { count: services.length })}
            </h2>
            <div className="space-y-2">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="p-4 rounded-2xl bg-white border border-gray-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center flex-shrink-0">
                      <Package size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[14px] text-gray-900 mb-1">
                        {service.name}
                      </h3>
                      <p className="text-[13px] text-gray-600 line-clamp-2">
                        {service.description}
                      </p>
                      <span className="text-[11px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block mt-2">
                        {service.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {products.length === 0 && services.length === 0 && (
          <EmptyState
            icon={<Package size={32} />}
            title={t("visitor.no_products_services")}
            description={t("visitor.company_no_products_services")}
          />
        )}
      </div>
    </PageShell>
  );
}
